import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldX, BadgeCheck, MapPin, Calendar, Award, Loader2, ArrowLeft, ExternalLink } from "lucide-react";
import SEO from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { LOGO_URL } from "../data/mock";

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerificaSocio() {
  const { numero } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${API}/api/registro/verifica/${encodeURIComponent(numero)}`)
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => { if (mounted) setData({ valida: false, stato: "errore" }); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [numero]);

  return (
    <>
      <SEO
        title={`Verifica Tessera ${numero} - A.C.D.T.`}
        description="Verifica pubblica dell'autenticità della tessera A.C.D.T. del socio."
        path={`/verifica/${numero}`}
      />
      <Navbar />
      <main className="min-h-screen bg-gradient-to-b from-[#f5f7ff] via-white to-[#f5f7ff] py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#102a6b] hover:underline mb-6">
            <ArrowLeft className="h-4 w-4" /> Torna alla home
          </Link>

          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="A.C.D.T." className="h-16 w-16 mx-auto mb-4" />
            <h1 className="text-2xl md:text-3xl font-extrabold text-[#0a1f55]">Verifica Tessera Socio</h1>
            <p className="mt-2 text-[14px] text-slate-600">
              N° Tessera: <span data-testid="verifica-tessera-input" className="font-bold text-[#102a6b]">{numero}</span>
            </p>
          </div>

          {loading ? (
            <div data-testid="verifica-loading" className="rounded-2xl border border-slate-200 bg-white p-12 text-center">
              <Loader2 className="h-8 w-8 mx-auto animate-spin text-[#102a6b]" />
              <p className="mt-3 text-[13px] text-slate-500">Verifica in corso...</p>
            </div>
          ) : data?.valida ? (
            <div data-testid="verifica-valida" className="rounded-2xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-7 md:p-10 shadow-lg">
              <div className="flex items-center gap-3 mb-6 pb-5 border-b border-emerald-200">
                <div className="h-14 w-14 rounded-2xl bg-emerald-500 text-white flex items-center justify-center shadow-md">
                  <ShieldCheck className="h-7 w-7" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-emerald-700">Tessera Autentica</div>
                  <div className="text-xl font-extrabold text-[#0a1f55]">Iscrizione verificata</div>
                </div>
              </div>

              <div className="space-y-4">
                <Field icon={BadgeCheck} label="Nome socio" value={`${data.nome} ${data.cognome}`} testid="verifica-nome" />
                <Field icon={Calendar} label="Iscritto dal" value={data.iscritto_dal ? new Date(data.iscritto_dal).toLocaleDateString("it-IT", { year: "numeric", month: "long", day: "numeric" }) : "—"} testid="verifica-iscritto-dal" />
                {(data.citta || data.provincia) && (
                  <Field icon={MapPin} label="Sede" value={`${data.citta || ""}${data.citta && data.provincia ? " (" : ""}${data.provincia || ""}${data.citta && data.provincia ? ")" : ""}`} testid="verifica-sede" />
                )}
                {data.specializzazione && (
                  <Field icon={Award} label="Specializzazione" value={data.specializzazione} testid="verifica-specializzazione" />
                )}
                {data.iscritto_registro && (
                  <div data-testid="verifica-registro-badge" className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#f59e0b]/15 text-[#b45309] text-[12.5px] font-extrabold">
                    <Award className="h-4 w-4" /> Iscritto al Registro Nazionale
                  </div>
                )}
              </div>

              <div className="mt-7 pt-5 border-t border-emerald-200 flex flex-col sm:flex-row gap-3 items-center justify-between">
                <p className="text-[12px] text-slate-500 text-center sm:text-left">
                  Verifica eseguita il {new Date().toLocaleDateString("it-IT")} via portale ufficiale A.C.D.T.
                </p>
                <Link
                  to="/registro-nazionale"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#102a6b] hover:bg-[#0a1f55] text-white text-[12.5px] font-bold transition-colors"
                >
                  Registro Nazionale <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          ) : (
            <div data-testid="verifica-non-valida" className="rounded-2xl border-2 border-red-200 bg-gradient-to-br from-red-50 to-white p-7 md:p-10 text-center">
              <div className="h-16 w-16 mx-auto rounded-2xl bg-red-500 text-white flex items-center justify-center shadow-md mb-4">
                <ShieldX className="h-8 w-8" />
              </div>
              <h2 className="text-xl font-extrabold text-red-700">Tessera non valida</h2>
              <p className="mt-2 text-[13.5px] text-slate-600 max-w-md mx-auto">
                Il numero di tessera <strong>{numero}</strong> non corrisponde a nessun socio A.C.D.T. attivo.
                Se ritieni si tratti di un errore contatta la segreteria.
              </p>
              <Link to="/" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#102a6b] hover:bg-[#0a1f55] text-white text-[13px] font-bold">
                Torna alla home
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}

function Field({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <div className="h-10 w-10 rounded-lg bg-white border border-emerald-200 flex items-center justify-center flex-shrink-0">
        <Icon className="h-4 w-4 text-emerald-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10.5px] uppercase tracking-wider font-extrabold text-slate-500">{label}</div>
        <div className="text-[14.5px] font-bold text-[#0a1f55]">{value}</div>
      </div>
    </div>
  );
}
